import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  ArrowUpRight,
  Eye,
  EyeOff,
  Sparkles,
  Repeat,
  ChevronDown,
  Wallet,
  Landmark,
  CreditCard,
  Clock,
  Check,
} from "lucide-react";
import { api, formatMXN } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useScope } from "../context/ScopeContext";
import KpiCard from "../components/cards/KpiCard";
import TransactionCard from "../components/cards/TransactionCard";
import EmptyState from "../components/EmptyState";
import ScopeToggle from "../components/ScopeToggle";
import PayRecurrenceSheet from "../components/PayRecurrenceSheet";
import IncomingInvitationBanner from "../components/IncomingInvitationBanner";

const MONTH_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

const ACCOUNT_ICONS = { efectivo: Wallet, banco: Landmark, tarjeta: CreditCard };

export default function Dashboard() {
  const { user } = useAuth();
  const { scope, partner } = useScope();
  const [summary, setSummary] = useState(null);
  const [recentTx, setRecentTx] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [pending, setPending] = useState([]);
  const [payTarget, setPayTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, t, b, p] = await Promise.all([
        api.get("/dashboard/summary", { params: { scope } }),
        api.get("/transactions", { params: { scope } }),
        api.get("/dashboard/accounts-breakdown", { params: { scope } }),
        api.get("/recurrences/pending", { params: { scope, days_forward: 3 } }),
      ]);
      setSummary(s.data);
      setRecentTx(t.data.slice(0, 5));
      setBreakdown(b.data.accounts || []);
      setPending(p.data || []);
    } catch (_) {
      setSummary(null);
      setRecentTx([]);
      setBreakdown([]);
      setPending([]);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    load();
    const onCreate = () => load();
    window.addEventListener("fp:tx-created", onCreate);
    window.addEventListener("fp:rec-changed", onCreate);
    return () => {
      window.removeEventListener("fp:tx-created", onCreate);
      window.removeEventListener("fp:rec-changed", onCreate);
    };
  }, [load]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Buenos días";
    if (h < 19) return "Buenas tardes";
    return "Buenas noches";
  }, []);

  const month = summary?.current_month;
  const balance = summary?.real_balance ?? 0;
  const positive = balance >= 0;
  const hasCarryOver =
    month && Math.abs(month.opening_balance) > 0.005;
  const monthLabel = month
    ? `${MONTH_ES[month.month - 1] || ""} ${month.year}`
    : "";

  return (
    <div className="animate-fade-up" data-testid="dashboard-screen">
      <header className="pt-10 pb-2 px-6 flex items-start justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-sage-700">
            {greeting}
          </p>
          <h1 className="font-display text-2xl font-extrabold tracking-tight mt-1">
            {scope === "shared"
              ? `Tú y ${partner?.name || "tu pareja"}`
              : user?.name?.split(" ")[0] || "Hola"}
          </h1>
        </div>
        <button
          type="button"
          onClick={() => setHidden((h) => !h)}
          data-testid="dashboard-toggle-balance"
          aria-label={hidden ? "Mostrar saldo" : "Ocultar saldo"}
          className="h-10 w-10 rounded-full bg-white border border-line text-soft hover:text-ink flex items-center justify-center"
        >
          {hidden ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </header>

      <div className="px-6 mt-3">
        <ScopeToggle />
      </div>

      {/* Hero balance */}
      <section className="px-6 mt-6" data-testid="real-balance-section">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-sage-700">
          {scope === "shared" ? "Dinero común disponible" : "Saldo real disponible"}
        </p>
        <div className="mt-2 flex items-baseline gap-2">
          <h2
            data-testid="real-balance-value"
            className={`font-display text-5xl sm:text-6xl font-extrabold tracking-tight tabular ${
              positive ? "text-ink" : "text-clay"
            }`}
          >
            {hidden ? "•••••" : formatMXN(balance)}
          </h2>
        </div>
        <p className="mt-2 text-sm text-muted2 flex items-center gap-1.5">
          <Sparkles size={14} className="text-sage-700" />
          {scope === "shared"
            ? (hasCarryOver
                ? "Dinero de los dos · heredado del mes pasado"
                : "Dinero de los dos · acumulado entre meses")
            : (hasCarryOver
                ? "Heredado del mes pasado · no se reinicia"
                : "Acumulado entre meses · no se reinicia")}
        </p>
      </section>

      {/* Monthly summary card */}
      <section className="px-6 mt-8" data-testid="month-summary-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold capitalize">
            {monthLabel || "Resumen del mes"}
          </h3>
          <div className="flex items-center gap-2">
            <Link
              to="/historial"
              data-testid="dashboard-historial-link"
              className="text-xs font-bold uppercase tracking-wider text-sage-700 inline-flex items-center gap-1 hover:text-sage-900"
            >
              Historial <ArrowUpRight size={12} />
            </Link>
            {month && (
              <span
                className={`text-[10px] font-bold uppercase tracking-[0.16em] px-2.5 py-1 rounded-full ${
                  month.is_closed
                    ? "bg-sage-900 text-bone"
                    : "bg-sage-50 text-sage-700"
                }`}
                data-testid="month-status-badge"
              >
                {month.is_current ? "En curso" : month.is_closed ? "Cerrado" : "Futuro"}
              </span>
            )}
          </div>
        </div>
        <div className="rounded-3xl bg-white border border-line/70 shadow-card p-5 space-y-3">
          <MonthRow
            label="Saldo inicial"
            value={hidden ? "•••" : formatMXN(month?.opening_balance ?? 0)}
            hint="Heredado del cierre anterior"
            testid="month-opening"
          />
          <Divider />
          <MonthRow
            label="Ingresos del mes"
            value={hidden ? "•••" : `+ ${formatMXN(month?.income ?? 0)}`}
            valueClass="text-sage-700"
            testid="month-income"
          />
          <MonthRow
            label="Gastos del mes"
            value={hidden ? "•••" : `− ${formatMXN(month?.expense ?? 0)}`}
            testid="month-expense"
          />
          <Divider />
          <MonthRow
            label="Saldo final"
            value={hidden ? "•••" : formatMXN(month?.closing_balance ?? 0)}
            valueClass={
              (month?.closing_balance ?? 0) < 0 ? "text-clay" : "text-ink"
            }
            bold
            testid="month-closing"
          />
        </div>
      </section>

      {/* Pendientes (recurrencias por pagar) */}
      <IncomingInvitationBanner />

      {pending.length > 0 && (
        <section className="px-6 mt-4" data-testid="pending-recurrences-section">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[10px] font-bold uppercase tracking-[0.18em] text-sage-700">
              Hoy toca pagar
            </h2>
            <span className="text-[10px] text-soft font-bold tabular">
              {pending.length} pendiente{pending.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="space-y-2">
            {pending.map((p) => (
              <PendingRow key={p.recurrence_id} item={p} onPay={() => setPayTarget(p)} />
            ))}
          </div>
        </section>
      )}

      {/* Per-account breakdown · collapsible */}
      {breakdown.length > 0 && (
        <section className="px-6 mt-4" data-testid="accounts-breakdown-section">
          <button
            type="button"
            onClick={() => setBreakdownOpen((v) => !v)}
            data-testid="accounts-breakdown-toggle"
            aria-expanded={breakdownOpen}
            className="w-full flex items-center justify-between bg-white border border-line/70 rounded-2xl px-5 py-3.5 hover:border-sage-200 transition-colors"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="h-8 w-8 rounded-xl bg-sage-50 text-sage-700 flex items-center justify-center shrink-0">
                <Wallet size={15} />
              </div>
              <div className="text-left min-w-0">
                <p className="text-sm font-bold text-ink">Por cuenta este mes</p>
                <p className="text-[11px] text-soft truncate">
                  {breakdown.length} cuenta{breakdown.length === 1 ? "" : "s"} · cómo se movió cada una
                </p>
              </div>
            </div>
            <ChevronDown
              size={18}
              className={`text-soft shrink-0 transition-transform ${breakdownOpen ? "rotate-180" : ""}`}
            />
          </button>

          {breakdownOpen && (
            <div
              className="mt-2 space-y-2 animate-fade-up"
              data-testid="accounts-breakdown-list"
            >
              {breakdown.map((a) => (
                <AccountBreakdownRow key={a.account_id} account={a} hidden={hidden} />
              ))}
              <p className="text-[11px] text-soft text-center pt-2 leading-relaxed">
                Entró/Salió incluye transferencias entre tus cuentas · La suma de saldos actuales coincide con tu saldo real.
              </p>
            </div>
          )}
        </section>
      )}

      {/* Period KPIs */}
      <section className="px-6 mt-8 grid grid-cols-2 gap-3" data-testid="period-kpis">
        <KpiCard
          label={scope === "shared" ? "Entró al común" : "Ingresos mes"}
          value={hidden ? "•••" : summary?.income_period ?? 0}
          icon={TrendingUp}
          tone="positive"
          testid="kpi-income"
        />
        <KpiCard
          label={scope === "shared" ? "Salió del común" : "Gastos mes"}
          value={hidden ? "•••" : summary?.expense_period ?? 0}
          icon={TrendingDown}
          tone="neutral"
          testid="kpi-expense"
        />
      </section>

      {/* Projections */}
      <section className="px-6 mt-8" data-testid="projections-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold">Proyección</h3>
          <Link
            to="/proyeccion"
            data-testid="dashboard-proyeccion-link"
            className="text-xs font-bold uppercase tracking-wider text-sage-700 inline-flex items-center gap-1 hover:text-sage-900"
          >
            Ver detalle <ArrowUpRight size={12} />
          </Link>
        </div>
        <div className="space-y-2.5">
          <ProjectionRow
            label="En 7 días"
            value={hidden ? "•••" : formatMXN(summary?.projection_7d ?? 0)}
            testid="projection-7d"
          />
          <ProjectionRow
            label="En 15 días"
            value={hidden ? "•••" : formatMXN(summary?.projection_15d ?? 0)}
            testid="projection-15d"
          />
          <ProjectionRow
            label="A fin de mes"
            value={hidden ? "•••" : formatMXN(summary?.projection_eom ?? 0)}
            highlight
            testid="projection-eom"
          />
        </div>
        {summary?.recurrences_count > 0 ? (
          <p className="mt-2 text-xs text-soft flex items-center gap-1.5">
            <Repeat size={12} className="text-sage-700" />
            {scope === "shared"
              ? `Considera ${summary.recurrences_count} compromiso${summary.recurrences_count === 1 ? "" : "s"} fijo${summary.recurrences_count === 1 ? "" : "s"} del espacio común.`
              : `Considera ${summary.recurrences_count} gasto/ingreso fijo${summary.recurrences_count === 1 ? "" : "s"} activo${summary.recurrences_count === 1 ? "" : "s"}.`}
          </p>
        ) : (
          <Link
            to="/recurrencias"
            data-testid="dashboard-add-recurrence-link"
            className="mt-3 text-xs text-sage-700 font-bold uppercase tracking-wider inline-flex items-center gap-1 hover:text-sage-900"
          >
            Agregar gastos fijos para mejor proyección <ArrowUpRight size={12} />
          </Link>
        )}
      </section>

      {/* Recent activity */}
      <section className="px-6 mt-8" data-testid="recent-activity-section">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display text-lg font-bold">
            {scope === "shared" ? "Movimientos del común" : "Movimientos recientes"}
          </h3>
          <Link
            to="/movimientos"
            data-testid="dashboard-movimientos-link"
            className="text-xs font-bold uppercase tracking-wider text-sage-700 inline-flex items-center gap-1 hover:text-sage-900"
          >
            Ver todos <ArrowUpRight size={12} />
          </Link>
        </div>

        <div className="bg-white border border-line/70 rounded-3xl shadow-card px-5">
          {loading ? (
            <div className="py-10 text-center text-soft text-sm">Cargando…</div>
          ) : recentTx.length === 0 ? (
            <EmptyState
              icon={Calendar}
              title={scope === "shared" ? "Aún sin movimientos compartidos" : "Aún sin movimientos"}
              description={scope === "shared"
                ? "Cuando registren ingresos o gastos en una cuenta compartida, aparecerán aquí."
                : "Agrega tu primer ingreso o gasto con el botón verde."}
              testid="dashboard-empty-tx"
            />
          ) : (
            recentTx.map((t) => (
              <TransactionCard key={t.id} tx={t} testid={`recent-tx-${t.id}`} />
            ))
          )}
        </div>
      </section>

      <PayRecurrenceSheet
        item={payTarget}
        onClose={() => setPayTarget(null)}
        onPaid={() => { setPayTarget(null); load(); }}
      />
    </div>
  );
}

function PendingRow({ item, onPay }) {
  const overdue = item.status === "overdue";
  const today = item.status === "today";
  const dueLabel = overdue
    ? `Vencido hace ${item.days_diff} día${item.days_diff === 1 ? "" : "s"}`
    : today
    ? "Vence hoy"
    : `Vence en ${item.days_diff} día${item.days_diff === 1 ? "" : "s"}`;
  const pillClass = overdue
    ? "bg-clay/15 text-clay"
    : today
    ? "bg-sage-100 text-sage-900"
    : "bg-bone text-soft";
  return (
    <div
      data-testid={`pending-row-${item.recurrence_id}`}
      className={`bg-white border rounded-2xl px-4 py-3.5 flex items-center gap-3 ${
        overdue ? "border-clay/40" : "border-line/70"
      }`}
    >
      <div className="h-10 w-10 rounded-xl bg-sage-50 text-sage-700 flex items-center justify-center shrink-0">
        <Clock size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-ink truncate">{item.name}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-[0.10em] rounded-full px-1.5 py-0.5 ${pillClass}`}>
            {dueLabel}
          </span>
          <span className="text-[11px] text-soft tabular">{formatMXN(item.amount)}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={onPay}
        data-testid={`pending-pay-${item.recurrence_id}`}
        className="h-9 px-3 rounded-full bg-sage-900 text-bone text-xs font-bold inline-flex items-center gap-1.5 hover:bg-sage-900/90 active:scale-95 transition-all shrink-0"
      >
        <Check size={12} strokeWidth={2.8} /> Pagar
      </button>
    </div>
  );
}

function Divider() {
  return <div className="h-px bg-line/70" />;
}

function MonthRow({ label, value, hint, valueClass = "text-ink", bold = false, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-3" data-testid={testid}>
      <div>
        <p className={`text-sm ${bold ? "font-bold text-ink" : "text-muted2"}`}>{label}</p>
        {hint && <p className="text-[11px] text-soft">{hint}</p>}
      </div>
      <p
        className={`font-display tabular ${bold ? "text-2xl font-extrabold" : "text-base font-bold"} ${valueClass}`}
      >
        {value}
      </p>
    </div>
  );
}

function ProjectionRow({ label, value, highlight, testid }) {
  return (
    <div
      className={`flex items-center justify-between rounded-2xl px-5 py-4 border ${
        highlight ? "bg-sage-900 text-bone border-sage-900" : "bg-white border-line/70"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <div
          className={`h-9 w-9 rounded-xl flex items-center justify-center ${
            highlight ? "bg-bone/15 text-bone" : "bg-sage-50 text-sage-700"
          }`}
        >
          <Calendar size={16} />
        </div>
        <span className={`text-sm font-semibold ${highlight ? "text-bone" : "text-ink"}`}>
          {label}
        </span>
      </div>
      <span className={`font-display font-extrabold tabular ${highlight ? "text-bone" : "text-ink"}`}>
        {value}
      </span>
    </div>
  );
}

function AccountBreakdownRow({ account, hidden }) {
  const Icon = ACCOUNT_ICONS[account.type] || Wallet;
  const negative = account.current_balance < 0;
  return (
    <div
      className="bg-white border border-line/70 rounded-2xl px-4 py-3.5"
      data-testid={`account-breakdown-${account.account_id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="h-9 w-9 rounded-xl bg-sage-50 text-sage-700 flex items-center justify-center shrink-0">
            <Icon size={15} />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-soft">
              {account.type}
            </p>
            <p className="text-sm font-bold text-ink truncate">{account.name}</p>
          </div>
        </div>
        <p
          className={`font-display text-lg font-extrabold tabular shrink-0 ${
            negative ? "text-clay" : "text-ink"
          }`}
        >
          {hidden ? "•••" : formatMXN(account.current_balance)}
        </p>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <BreakdownStat
          label="Inicial"
          value={hidden ? "•••" : formatMXN(account.opening_balance)}
        />
        <BreakdownStat
          label="Entró"
          value={hidden ? "•••" : `+ ${formatMXN(account.month_in)}`}
          tone={account.month_in > 0 ? "positive" : "muted"}
        />
        <BreakdownStat
          label="Salió"
          value={hidden ? "•••" : `− ${formatMXN(account.month_out)}`}
          tone={account.month_out > 0 ? "negative" : "muted"}
        />
      </div>
    </div>
  );
}

function BreakdownStat({ label, value, tone = "muted" }) {
  const valueClass =
    tone === "positive" ? "text-sage-700"
      : tone === "negative" ? "text-clay"
      : "text-ink";
  return (
    <div className="bg-bone/60 rounded-xl px-2.5 py-2 text-center">
      <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-soft">
        {label}
      </p>
      <p className={`text-xs font-bold tabular mt-0.5 ${valueClass}`}>{value}</p>
    </div>
  );
}
